import React, { useEffect, useState, useRef } from "react";
import StillTelevision from "./StillTelevision";
import TVGallery from "./TVGallery";
import "./Info.css";

const infoText = `I work on AI and messaging infrastructure in Berlin, between software, business, and the people using it.`;

const hobbyImages = [
  "/hobbies/IMG_6215(1).jpg",
  "/hobbies/me.jpeg",
  "/hobbies/bb4cb569-c469-4ca4-a825-9960ad865329.avif",
];

const imageLocations = [
  "三亚",
  "上海",
  "San Sebastián",
];

const CYCLE_INTERVAL = 3500;
const FADE_DURATION = 700;

// TV geometry — fat bezel, no stand
const SW = 256;   // screen width
const SH = 192;   // screen height (4:3)
const BZ = 34;    // bezel thickness (top + sides)
const CHN = 52;    // bottom chin height
const TW = SW + BZ * 2;        // total TV width  = 324
const TH = BZ + SH + CHN;      // total TV height = 278
const DEP = 52;                  // depth of the TV body

// Speaker dot grid helper
function SpeakerGrid({ cols = 4, rows = 5 }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${cols}, 5px)`, gap: "4px" }}>
      {Array.from({ length: cols * rows }).map((_, i) => (
        <div
          key={i}
          style={{
            width: 4, height: 4, borderRadius: "50%",
            background: "rgba(0,0,0,0.55)",
            boxShadow: "inset 0 1px 1px rgba(0,0,0,0.5)",
          }}
        />
      ))}
    </div>
  );
}

// Round TV knob
function Knob({ label }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
      <div style={{
        width: 18, height: 18, borderRadius: "50%",
        background: "radial-gradient(circle at 35% 35%, #4a4540, #1a1715)",
        boxShadow: "0 2px 4px rgba(0,0,0,0.7), inset 0 1px 1px rgba(255,255,255,0.1), 0 0 0 2px #0d0c0b",
        position: "relative",
      }}>
        {/* Indicator line */}
        <div style={{
          position: "absolute", top: 2, left: "50%",
          width: 1.5, height: 6,
          background: "rgba(255,255,255,0.35)",
          transform: "translateX(-50%)",
          borderRadius: 1,
        }} />
      </div>
      <span style={{
        fontFamily: "'Share Tech Mono', monospace",
        fontSize: 6, color: "rgba(255,255,255,0.25)",
        letterSpacing: "0.1em", userSelect: "none",
      }}>{label}</span>
    </div>
  );
}

export default function Info() {
  const [time, setTime] = useState("");
  const [current, setCurrent] = useState(0);
  const [prev, setPrev] = useState(null);
  const [fading, setFading] = useState(false);
  const [glitch, setGlitch] = useState(false);
  const [trackingShift, setTrackingShift] = useState(false);

  const currentRef = useRef(0);
  const timerRef = useRef(null);
  const fadeRef = useRef(null);

  useEffect(() => {
    const tick = () => setTime(new Date().toLocaleTimeString("en-GB", { hour12: false }));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    let t;
    const schedule = () => {
      t = setTimeout(() => {
        setGlitch(true);
        setTimeout(() => setGlitch(false), 100 + Math.random() * 170);
        schedule();
      }, 2800 + Math.random() * 5000);
    };
    schedule();
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    let t;
    const schedule = () => {
      t = setTimeout(() => {
        setTrackingShift(true);
        setTimeout(() => setTrackingShift(false), 300 + Math.random() * 350);
        schedule();
      }, 4500 + Math.random() * 7000);
    };
    schedule();
    return () => clearTimeout(t);
  }, []);

  const advanceTo = (next) => {
    setPrev(currentRef.current);
    setFading(true);
    clearTimeout(fadeRef.current);
    fadeRef.current = setTimeout(() => {
      currentRef.current = next;
      setCurrent(next);
      setPrev(null);
      setFading(false);
    }, FADE_DURATION);
  };

  useEffect(() => {
    timerRef.current = setInterval(() => {
      advanceTo((currentRef.current + 1) % hobbyImages.length);
    }, CYCLE_INTERVAL);
    return () => { clearInterval(timerRef.current); clearTimeout(fadeRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDotClick = (i) => {
    if (i === currentRef.current || fading) return;
    clearInterval(timerRef.current);
    clearTimeout(fadeRef.current);
    advanceTo(i);
    timerRef.current = setInterval(() => {
      advanceTo((currentRef.current + 1) % hobbyImages.length);
    }, CYCLE_INTERVAL);
  };

  const now = new Date();
  const camDate = now
    .toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
    .toUpperCase().replace(",", "");

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Share+Tech+Mono&display=swap');
        @keyframes scanln {
          from { background-position: 0 0; }
          to   { background-position: 0 4px; }
        }
        @keyframes vhsGlitch {
          0%   { clip-path: inset(28% 0 62% 0); transform: translateX(-7px); }
          30%  { clip-path: inset(52% 0 22% 0); transform: translateX(9px);  }
          60%  { clip-path: inset(12% 0 78% 0); transform: translateX(-5px); }
          100% { clip-path: inset(42% 0 44% 0); transform: translateX(6px);  }
        }
        @keyframes trackBar {
          0%   { transform: translateY(-110%); opacity: 0; }
          20%  { opacity: 0.55; }
          80%  { opacity: 0.4; }
          100% { transform: translateY(110%);  opacity: 0; }
        }
        @keyframes noiseShift {
          0%  { background-position: 0 0; }
          25% { background-position: -7% 13%; }
          50% { background-position: 9% -16%; }
          75% { background-position: -11% 7%; }
          100%{ background-position: 0 0; }
        }
        @keyframes locationFade {
          0%  { opacity: 0; transform: translateY(4px); }
          15% { opacity: 1; transform: translateY(0); }
          85% { opacity: 1; }
          100%{ opacity: 0; }
        }
        @keyframes nameVHS {
          0%   { opacity: 0; }
          6%   { opacity: 0.9; }
          9%   { opacity: 0.1; }
          14%  { opacity: 1; }
          17%  { opacity: 0; }
          22%  { opacity: 0.8; }
          25%  { opacity: 0.2; }
          30%  { opacity: 1; }
          100% { opacity: 1; }
        }
        @keyframes cardFlicker {
          0%   { opacity: 0; }
          10%  { opacity: 0.9; }
          16%  { opacity: 0.1; }
          24%  { opacity: 1; }
          29%  { opacity: 0.3; }
          36%  { opacity: 1; }
          100% { opacity: 1; }
        }
        .name-vhs {
          animation: nameVHS 1.6s ease both;
        }
        .card-line-1 { animation: cardFlicker 1.2s ease 0.1s both; }
        .card-line-2 { animation: cardFlicker 1.2s ease 0.6s both; }
        .card-line-3 { animation: cardFlicker 1.2s ease 1.1s both; }
        .card-line-4 { animation: cardFlicker 1.2s ease 1.6s both; }
        .vhs-img { filter: contrast(1.12) saturate(0.6) brightness(0.78) sepia(0.22); }
        .vhs-glitch {
          animation: vhsGlitch 0.1s steps(1) infinite;
          filter: contrast(1.4) saturate(0.4) hue-rotate(20deg);
          mix-blend-mode: hard-light;
        }
        .track-bar { animation: trackBar 0.42s linear forwards; }
        .mono { font-family: 'Share Tech Mono', 'Courier New', monospace; }
        .loc-tag { animation: locationFade ${CYCLE_INTERVAL}ms ease forwards; }
        .pom-link {
          position: absolute;
          top: 1.5rem;
          right: 1.5rem;
          font-size: 11px;
          letter-spacing: 0.1em;
          color: rgba(163,163,163,0.7);
          text-decoration: none;
          transition: all 0.2s ease;
          font-family: system-ui, -apple-system, sans-serif;
        }
        .pom-link:hover { transform: scale(1.08); color: #f97316; }
      `}</style>

      <div className="home-page">
        <header className="home-header">
        <nav className="home-socials" aria-label="Contact and profiles">
          {/* Mail */}
          <a
            href="mailto:edgarhipp@protonmail.com"
            title="Email"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 32, height: 32,
              borderRadius: "8px",
              background: "rgba(80,75,70,0.12)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              color: "rgba(90,87,83,0.9)",
              border: "1px solid rgba(100,95,90,0.18)",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              transition: "transform 0.18s ease, background 0.18s ease",
              textDecoration: "none",
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.12)"; e.currentTarget.style.background = "rgba(80,75,70,0.22)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = "rgba(80,75,70,0.12)"; }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <rect x="2" y="4" width="20" height="16" rx="2" />
              <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
            </svg>
          </a>

          {/* CV */}
          <a
            href="/Resume___one_page-14.pdf"
            download
            title="Download CV"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              height: 32,
              padding: "0 10px",
              borderRadius: "8px",
              background: "rgba(80,75,70,0.12)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              color: "rgba(90,87,83,0.9)",
              border: "1px solid rgba(100,95,90,0.18)",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              transition: "transform 0.18s ease, background 0.18s ease",
              textDecoration: "none",
              fontFamily: "'Share Tech Mono', monospace",
              fontSize: 11,
              letterSpacing: "0.1em",
              fontWeight: 600,
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.12)"; e.currentTarget.style.background = "rgba(80,75,70,0.22)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = "rgba(80,75,70,0.12)"; }}
          >
            cv
          </a>

          {/* X / Twitter */}
          <a
            href="https://x.com/Edgar_Hipp"
            target="_blank"
            rel="noreferrer"
            title="X / Twitter"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 32, height: 32,
              borderRadius: "8px",
              background: "rgba(80,75,70,0.12)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              color: "rgba(90,87,83,0.9)",
              border: "1px solid rgba(100,95,90,0.18)",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              transition: "transform 0.18s ease, background 0.18s ease",
              textDecoration: "none",
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.12)"; e.currentTarget.style.background = "rgba(80,75,70,0.22)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = "rgba(80,75,70,0.12)"; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.741l7.73-8.835L2.25 2.25h6.893l4.255 5.623zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
            </svg>
          </a>

          {/* GitHub */}
          <a
            href="https://github.com/EdgarWHipp"
            target="_blank"
            rel="noreferrer"
            title="GitHub"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 32, height: 32,
              borderRadius: "8px",
              background: "rgba(80,75,70,0.12)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              color: "rgba(90,87,83,0.9)",
              border: "1px solid rgba(100,95,90,0.18)",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              transition: "transform 0.18s ease, background 0.18s ease",
              textDecoration: "none",
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.12)"; e.currentTarget.style.background = "rgba(80,75,70,0.22)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = "rgba(80,75,70,0.12)"; }}
          >
            <svg width="15" height="15" viewBox="0 0 24 24" fill="currentColor">
              <path d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0 1 12 6.844a9.59 9.59 0 0 1 2.504.337c1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.02 10.02 0 0 0 22 12.017C22 6.484 17.522 2 12 2z" />
            </svg>
          </a>

          {/* LinkedIn */}
          <a
            href="https://www.linkedin.com/in/edgar-hipp-b850aa200/"
            target="_blank"
            rel="noreferrer"
            title="LinkedIn"
            style={{
              display: "flex", alignItems: "center", justifyContent: "center",
              width: 32, height: 32,
              borderRadius: "8px",
              background: "rgba(80,75,70,0.12)",
              backdropFilter: "blur(8px)",
              WebkitBackdropFilter: "blur(8px)",
              color: "rgba(90,87,83,0.9)",
              border: "1px solid rgba(100,95,90,0.18)",
              boxShadow: "0 1px 3px rgba(0,0,0,0.12)",
              transition: "transform 0.18s ease, background 0.18s ease",
              textDecoration: "none",
            }}
            onMouseEnter={e => { e.currentTarget.style.transform = "scale(1.12)"; e.currentTarget.style.background = "rgba(80,75,70,0.22)"; }}
            onMouseLeave={e => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.background = "rgba(80,75,70,0.12)"; }}
          >
            <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
            </svg>
          </a>
        <a className="home-phone" href="tel:+493082686193" aria-label="Call 030 82686193" title="030 82686193">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6A19.79 19.79 0 0 1 2.12 4.2 2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.12.96.36 1.9.7 2.79a2 2 0 0 1-.45 2.11L8.09 9.89a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.89.34 1.83.58 2.79.7A2 2 0 0 1 22 16.92z" />
          </svg>
        </a>
        </nav>
        </header>

        <main className="home-main">
          <div className="home-intro">
            <h1>Hi, I’m Eddy.</h1>
            <p>{infoText}</p>
          </div>

          <TVGallery>
          <StillTelevision
            color="green"
            image="/hobbies/IMG_8661.jpg"
            alt="Cable cars above Fuzhou, with Edgar in the foreground"
            caption="02 / 福州"
            channel="02"
          />
          <div className="tv-original" style={{ perspective: "950px", perspectiveOrigin: "50% 45%" }}>
            <div
              style={{
                position: "relative",
                width: `${TW}px`,
                height: `${TH}px`,
                transformStyle: "preserve-3d",
                /* Only rotate on Y — no X tilt, so top face never shows */
                transform: "rotateY(-13deg)",
                margin: "0 auto",
              }}
            >

              {/* ══ FRONT FACE ══ */}
              <div
                style={{
                  position: "absolute",
                  width: `${TW}px`,
                  height: `${TH}px`,
                  transform: `translateZ(${DEP / 2}px)`,
                  /* Dark charcoal plastic with subtle gradient */
                  background: "linear-gradient(160deg, #2e2b27 0%, #201e1b 55%, #181614 100%)",
                  borderRadius: "14px",
                  boxShadow:
                    "inset 0 2px 8px rgba(255,255,255,0.06), inset 0 -3px 10px rgba(0,0,0,0.5)",
                }}
              >
                {/* ── Raised inner bezel around screen ── */}
                <div
                  style={{
                    position: "absolute",
                    top: BZ - 6,
                    left: BZ - 6,
                    width: SW + 12,
                    height: SH + 12,
                    background: "#111010",
                    borderRadius: "6px",
                    boxShadow: "inset 0 2px 6px rgba(0,0,0,0.8), 0 2px 6px rgba(0,0,0,0.5)",
                  }}
                />

                {/* ── Screen ── */}
                <div
                  style={{
                    position: "absolute",
                    top: BZ,
                    left: BZ,
                    width: `${SW}px`,
                    height: `${SH}px`,
                    overflow: "hidden",
                    background: "#030303",
                    borderRadius: "4px",
                    boxShadow: "inset 0 0 28px rgba(0,0,0,0.98)",
                  }}
                >
                  {/* Prev image */}
                  {prev !== null && (
                    <img
                      src={hobbyImages[prev]} alt="" aria-hidden="true"
                      className="vhs-img"
                      style={{
                        position: "absolute", inset: 0,
                        width: "100%", height: "100%",
                        objectFit: "cover", zIndex: 1,
                        opacity: 0,
                        transition: `opacity ${FADE_DURATION}ms ease`,
                      }}
                    />
                  )}

                  {/* Current image */}
                  <img
                    src={hobbyImages[current]}
                    alt={`Hobby ${current + 1}`}
                    className="vhs-img"
                    style={{
                      position: "absolute", inset: 0,
                      width: "100%", height: "100%",
                      objectFit: "cover", zIndex: 2,
                      opacity: fading ? 0 : 1,
                      transition: `opacity ${FADE_DURATION}ms ease`,
                    }}
                  />

                  {/* Chromatic aberration */}
                  <img
                    src={hobbyImages[current]} alt="" aria-hidden="true"
                    style={{
                      position: "absolute", inset: 0,
                      width: "100%", height: "100%",
                      objectFit: "cover", zIndex: 3,
                      opacity: 0.15, mixBlendMode: "screen",
                      filter: "saturate(0.3) contrast(1.1)",
                      transform: "translateX(3px)",
                      pointerEvents: "none",
                    }}
                  />

                  {/* Glitch */}
                  {glitch && (
                    <img
                      src={hobbyImages[current]} alt="" aria-hidden="true"
                      className="vhs-glitch"
                      style={{
                        position: "absolute", inset: 0,
                        width: "100%", height: "100%",
                        objectFit: "cover", zIndex: 8,
                        pointerEvents: "none",
                      }}
                    />
                  )}

                  {/* Tracking bar */}
                  {trackingShift && (
                    <div className="track-bar" style={{
                      position: "absolute", left: 0, right: 0, top: 0,
                      height: "22%", zIndex: 9, pointerEvents: "none",
                      background:
                        "linear-gradient(to bottom, rgba(255,255,255,0.09), rgba(220,220,200,0.04), transparent)",
                      filter: "blur(0.5px)",
                    }} />
                  )}

                  {/* Scanlines */}
                  <div style={{
                    position: "absolute", inset: 0, zIndex: 10, pointerEvents: "none",
                    backgroundImage:
                      "repeating-linear-gradient(0deg, rgba(0,0,0,0.25) 0px, rgba(0,0,0,0.25) 1px, transparent 1px, transparent 4px)",
                    backgroundSize: "100% 4px",
                    animation: "scanln 0.22s linear infinite",
                  }} />

                  {/* Noise */}
                  <div style={{
                    position: "absolute", inset: "-50%", zIndex: 11,
                    width: "200%", height: "200%",
                    opacity: 0.055, pointerEvents: "none",
                    backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
                    backgroundSize: "180px",
                    animation: "noiseShift 0.35s steps(1) infinite",
                  }} />

                  {/* Vignette */}
                  <div style={{
                    position: "absolute", inset: 0, zIndex: 12, pointerEvents: "none",
                    background:
                      "radial-gradient(ellipse at center, transparent 48%, rgba(0,0,0,0.65) 100%)",
                  }} />

                  {/* Screen glare */}
                  <div style={{
                    position: "absolute", inset: 0, zIndex: 13, pointerEvents: "none",
                    background:
                      "linear-gradient(130deg, rgba(255,255,255,0.05) 0%, transparent 45%)",
                  }} />

                  {/* OSD — PLAY */}
                  <div className="mono" style={{
                    position: "absolute", top: 6, left: 8, zIndex: 16,
                    fontSize: "8px", letterSpacing: "0.1em",
                    color: "rgba(255,255,200,0.75)", userSelect: "none",
                  }}>
                    ▶ PLAY
                  </div>

                  {/* OSD — timestamp + location */}
                  <div className="mono" style={{
                    position: "absolute", bottom: 28, left: 7, zIndex: 16,
                    fontSize: "8px", letterSpacing: "0.07em", lineHeight: 1.6,
                    color: "rgba(255,255,200,0.82)",
                    textShadow: "0 0 4px rgba(255,255,150,0.5)",
                    userSelect: "none",
                  }}>
                    <div>{camDate}</div>
                    <div>{time}</div>
                    {/* Location tag — keyed to current so animation restarts per slide */}
                    <div
                      key={current}
                      className="loc-tag"
                      style={{ marginTop: 2, color: "rgba(200,240,255,0.9)" }}
                    >
                      {imageLocations[current]}
                    </div>
                  </div>

                  {/* Dot indicators */}
                  <div style={{
                    position: "absolute", bottom: 7, left: 0, right: 0,
                    display: "flex", justifyContent: "center", gap: 5, zIndex: 16,
                  }}>
                    {hobbyImages.map((_, i) => (
                      <div
                        key={i}
                        onClick={() => handleDotClick(i)}
                        style={{
                          width: i === current ? 14 : 5, height: 4,
                          background:
                            i === current ? "rgba(255,255,200,0.9)" : "rgba(255,255,255,0.22)",
                          transition: "width 0.35s ease, background 0.35s ease",
                          cursor: "pointer",
                        }}
                      />
                    ))}
                  </div>
                </div>
                {/* ── end screen ── */}

                {/* ── Chin / control panel ── */}
                <div style={{
                  position: "absolute",
                  top: BZ + SH + 10,
                  left: BZ,
                  width: SW,
                  height: CHN - 10,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}>
                  {/* Speaker grill */}
                  <div style={{ opacity: 0.6 }}>
                    <SpeakerGrid cols={5} rows={3} />
                  </div>

                  {/* Brand */}
                  <span className="mono" style={{
                    fontSize: "9px", letterSpacing: "0.25em",
                    color: "rgba(255,255,255,0.18)", userSelect: "none",
                  }}>
                    EH·8000
                  </span>

                  {/* Knobs */}
                  <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                    <Knob label="VOL" />
                    <Knob label="CH" />
                  </div>
                </div>

              </div>
              {/* ══ end FRONT FACE ══ */}

              {/* ══ RIGHT FACE only ══ */}
              <div style={{
                position: "absolute",
                top: 0,
                width: `${DEP}px`,
                height: `${TH}px`,
                background:
                  "linear-gradient(to right, #1c1a17 0%, #141210 70%, #0e0d0b 100%)",
                transform: `rotateY(90deg) translateZ(${TW / 2}px)`,
                borderRadius: "0 14px 14px 0",
              }} />



            </div>
            <p className="tv-original-caption">01 / {imageLocations.join(" / ")}</p>
          </div>
          <StillTelevision
            color="red"
            image="/hobbies/IMG_8418.jpg"
            alt="Edgar photographing a drawing in a quiet room in Fuzhou"
            caption="03 / 福州"
            channel="03"
          />
          </TVGallery>
        </main>

        <footer className="home-footer">
          <p>Edgar Hipp<br />Berlin, Germany</p>
          <p>© {new Date().getFullYear()}<br />No rights reserved.</p>
        </footer>
      </div>
    </>
  );
}
